import { createTusUploader } from './uploaders/tus';
import { delay } from './utils';
/**
 * VideoUploadManager will manage video uploading process,
 * to queue and retry the videos to upload, for better experience.
 *
 * To use this manager:
 * 1. Create a new instance of VideoUploadManager
 * 2. Call `addUploadJob` method to add a video into the queue.
 * 3. After adding all videos to upload, call `start` method.
 */
export class VideoUploadManager {
    options;
    uploadIds;
    jobQueue;
    jobsByUploadId;
    currentJobIndex = -1;
    currentJob = null;
    currentUploader;
    createUploader;
    started;
    /**
     *
     * @param options Uploading options
     * @param createUploader Optional parameter to custom how uploader is created. Usually use for testing only.
     */
    constructor(options, createUploader) {
        this.options = options;
        this.createUploader = createUploader || createTusUploader;
        this.jobQueue = new Array();
        this.jobsByUploadId = new Map();
    }
    /**
     * Replace the options with the new one.
     * @param newOptions New opptions.
     */
    setOptions(newOptions) {
        if (this.started) {
            throw new Error('Cannot set new options when upload manager is running.');
        }
        this.options = newOptions;
    }
    /**
     * Add a upload job to the queue.
     * Use this function to upload a file to existing video resource on ByteArk Stream or Qoder.
     *
     * @param uploadId Upload ID. Use "video.object.source_id" for ByteArk Qoder.
     * @param file File instance to upload
     */
    addUploadJob(uploadId, file) {
        const job = {
            uploadId,
            file,
            name: file.name,
            status: 'pending',
        };
        this.jobsByUploadId.set(job.uploadId, job);
        this.jobQueue.push(job);
    }
    /**
     * Get existing job by upload id.
     *
     * @param uploadId Upload ID. It'll be "video key" for ByteArk Stream, or "video source id" for ByteArk Qoder.
     * @returns Detail of the upload job
     */
    getJobByUploadId(uploadId) {
        return this.jobsByUploadId.get(uploadId);
    }
    /**
     * Start uploading.
     *
     * @returns Promise that will resolved after uploading has done.
     */
    async start() {
        if (this.started) {
            return;
        }
        this.started = true;
        while (this.started) {
            try {
                const result = await this.uploadNextJob();
                if (!result) {
                    this.started = false;
                }
            }
            catch (err) {
                console.error('Uploading failed', err);
            }
            await delay(1000);
        }
    }
    async uploadNextJob() {
        if (this.currentJobIndex >= this.jobQueue.length - 1) {
            return Promise.resolve(null);
        }
        this.currentJobIndex += 1;
        this.currentJob = this.jobQueue[this.currentJobIndex];
        this.currentUploader = this.createUploader(this.currentJob, this.options);
        return this.currentUploader.start();
    }
}
//# sourceMappingURL=data:application/json;base64,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